import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, UserPlus, UserCheck } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import PosterCard from "../components/PosterCard";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Navbar />
      {children}
    </div>
  );
}

export default function Person() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, patchUser } = useAuth();
  const { layout } = useTheme();
  const flatHead = layout.hero === "masthead";
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const following = (user?.following || []).some((x) => x.person_id === String(id));

  const toggleFollow = async () => {
    try {
      if (following) {
        const { data } = await api.delete(`/user/follow/${id}`);
        patchUser({ following: data.following });
        toast("Unfollowed");
      } else {
        const { data } = await api.post("/user/follow", { person_id: String(id), name: p?.name || "", photo: p?.photo || null });
        patchUser({ following: data.following });
        toast.success(`Following ${p?.name || "actor"}`);
      }
    } catch {
      toast.error("Could not update follow");
    }
  };

  useEffect(() => {
    setP(null);
    setNotFound(false);
    api.get(`/catalog/person/${id}`).then(({ data }) => setP(data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return <Shell><div className="flex h-[60vh] items-center justify-center text-[var(--text-secondary)]" data-testid="person-not-found">Person not found.</div></Shell>;
  }
  if (!p) {
    return <Shell><div className="flex h-[60vh] items-center justify-center font-head text-2xl tracking-widest text-[var(--accent)] animate-pulse">Loading…</div></Shell>;
  }

  const noServices = (user?.services || []).length === 0;
  const onYours = p.titles.filter((t) => t.on_your_services);
  const rest = p.titles.filter((t) => !t.on_your_services);
  const head = (extra) => `font-head text-2xl text-[var(--text-primary)] ${flatHead ? "font-bold" : "tracking-wide"} ${extra || ""}`;

  return (
    <Shell>
      <div className="mx-auto max-w-6xl px-4 lg:px-14 py-8" data-testid="person-page">
        <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="person-back">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-5">
          {p.photo ? (
            <img src={p.photo} alt={p.name} className="h-24 w-24 flex-none rounded-full border-2 border-[var(--border-subtle)] object-cover" data-testid="person-photo" />
          ) : (
            <div className="flex h-24 w-24 flex-none items-center justify-center rounded-full bg-[var(--accent)]/20 font-head text-4xl text-[var(--accent)]">{(p.name || "?").charAt(0)}</div>
          )}
          <div>
            <h1 className={`font-head ${flatHead ? "font-bold" : "tracking-wide"} text-4xl text-[var(--text-primary)]`} data-testid="person-name">{p.name}</h1>
            {p.department && <p className="mt-1 text-sm uppercase tracking-wider text-[var(--text-secondary)]">{p.department}</p>}
            <button onClick={toggleFollow}
              className={`mt-3 inline-flex items-center gap-2 rounded-[var(--card-radius)] px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors ${following ? "border border-[var(--border-accent)] bg-[var(--bg-card)] text-[var(--text-primary)]" : "bg-[var(--accent)] text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]"}`}
              data-testid="person-follow-toggle">
              {following ? <><UserCheck size={16} /> Following</> : <><UserPlus size={16} /> Follow</>}
            </button>
          </div>
        </div>

        {onYours.length > 0 && (
          <section className="mt-10" data-testid="person-on-your-services">
            <h2 className={head()}>Available on your services</h2>
            <div className="mt-4 flex flex-wrap gap-4">
              {onYours.map((t) => <PosterCard key={t.id} item={t} />)}
            </div>
          </section>
        )}

        {noServices && p.titles.length > 0 && (
          <p className="mt-8 text-sm text-[var(--text-secondary)]" data-testid="person-pick-services">
            <Link to="/settings" className="font-semibold text-[var(--accent-hover)] underline">Pick the services you pay for</Link> in Settings to see which of these you can watch.
          </p>
        )}

        {!noServices && onYours.length === 0 && p.titles.length > 0 && (
          <p className="mt-8 text-sm text-[var(--text-secondary)]" data-testid="person-none-on-services">
            None of {p.name}'s titles are on your selected services right now.
          </p>
        )}

        {rest.length > 0 && (
          <section className="mt-10" data-testid="person-more-titles">
            <h2 className={head()}>{onYours.length ? "More titles" : "Known for"}</h2>
            <div className="mt-4 flex flex-wrap gap-4">
              {rest.map((t) => <PosterCard key={t.id} item={t} />)}
            </div>
          </section>
        )}

        {p.titles.length === 0 && (
          <p className="mt-8 text-[var(--text-secondary)]" data-testid="person-no-titles">No titles found for {p.name}.</p>
        )}
      </div>
    </Shell>
  );
}
